// src/store/slices/vendorSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import vendorApi from 'api/vendorApi';

export const fetchVendors = createAsyncThunk(
  'vendor/fetchVendors',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await vendorApi.getAll(page, pageSize, search);
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load vendors';
      return rejectWithValue(msg);
    }
  }
);

export const fetchVendorDropdown = createAsyncThunk(
  'vendor/fetchVendorDropdown',
  async (_, { rejectWithValue }) => {
    try {
      const response = await vendorApi.getDropdown();
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load vendor dropdown';
      return rejectWithValue(msg);
    }
  }
);

export const createVendor = createAsyncThunk(
  'vendor/createVendor',
  async (data, { rejectWithValue }) => {
    try {
      const response = await vendorApi.create(data);
      return response;
    } catch (err) {
      const msg = err.response?.data || 'Failed to create vendor';
      console.log("msg", msg);
      return rejectWithValue(msg);
    }
  }
);

export const updateVendor = createAsyncThunk(
  'vendor/updateVendor',
  async ({ id, ...updates }, { rejectWithValue }) => {
    try {
      const response = await vendorApi.update(id, updates);
      return response;
    } catch (err) {
      const msg = err.response?.data?.name?.[0] || 'Failed to update vendor';
      return rejectWithValue(msg);
    }
  }
);

export const deleteVendor = createAsyncThunk(
  'vendor/deleteVendor',
  async (id, { rejectWithValue }) => {
    try {
      await vendorApi.delete(id);
      return id;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete vendor';
      return rejectWithValue(msg);
    }
  }
);

export const exportVendors = createAsyncThunk(
  'vendor/exportVendors',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await vendorApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendors_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

export const importPreview = createAsyncThunk(
  'vendor/importPreview',
  async (file, { rejectWithValue }) => {
    try {
      const data = await vendorApi.importPreview(file);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Preview failed');
    }
  }
);

export const importConfirm = createAsyncThunk(
  'vendor/importConfirm',
  async (preview, { rejectWithValue, dispatch }) => {
    try {
      await vendorApi.importConfirm(preview);
      dispatch(fetchVendors({ page: 1, pageSize: 10 }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Import failed');
    }
  }
);

const vendorSlice = createSlice({
  name: 'vendor',
  initialState: {
    vendors: [],
    dropdown: [],
    pagination: { next: null, previous: null, count: 0, page_size: 10 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendors.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vendors = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchVendorDropdown.fulfilled, (state, action) => {
        state.dropdown = action.payload;
      })

      .addCase(createVendor.fulfilled, (state, action) => {
        state.vendors.unshift(action.payload);
        state.pagination.count += 1;
      })

      .addCase(updateVendor.fulfilled, (state, action) => {
        const idx = state.vendors.findIndex((v) => v.id === action.payload.id);
        if (idx !== -1) state.vendors[idx] = action.payload;
      })

      .addCase(deleteVendor.fulfilled, (state, action) => {
        state.vendors = state.vendors.filter((v) => v.id !== action.payload);
        state.pagination.count -= 1;
      })

      .addCase(exportVendors.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError } = vendorSlice.actions;
export default vendorSlice.reducer;